import { useTranslation } from 'react-i18next';
import { FolderIconClosed } from '@/utils/icons';
import { PendingSpinner } from '../../PendingSpinner/PendingSpinner';
import type { INewFolder } from '../../hooks/useNewFolder';
import { RenameInput } from '../../RenameInput/RenameInput';
import styles from './DocumentGrid.module.css';

interface INewFolderCardProps {
  newFolder: INewFolder;
  value: string;
  error: string | null;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * The folder being created, shown among the folder cards but not a
 * FolderCard: with no id there is nothing to open, drag, drop onto or
 * right-click with, so none of those handlers exist here rather than being
 * disabled.
 */
export function NewFolderCard({ newFolder, value, error, onChange, onCommit, onCancel }: INewFolderCardProps) {
  const { t } = useTranslation();
  const isCreating = newFolder.status === 'creating';

  return (
    <div className={styles.card} aria-busy={isCreating ? true : undefined}>
      <span className={styles.preview} aria-hidden="true">
        <span className={styles.glyph}><FolderIconClosed size={44} /></span>
      </span>
      <span className={styles.caption}>
        {/* While naming, the (invisible) name holds the card's height and the
            editor floats over it — the same trick the rename cards use. */}
        <span className={`${styles.name} ${isCreating ? '' : styles.ghost}`}>
          {newFolder.name}
          {isCreating && <PendingSpinner />}
        </span>
        <span className={`${styles.meta} ${isCreating ? '' : styles.ghost}`}>{t('documents.card.folder')}</span>
        {!isCreating && (
          <span className={styles.renameOverlay}>
            <RenameInput value={value} error={error} onChange={onChange} onCommit={onCommit} onCancel={onCancel} />
          </span>
        )}
      </span>
    </div>
  );
}
